import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { fadeInRight400ms } from 'src/@core/animations/fade-in-right.animation';
import { fadeInUp400ms } from 'src/@core/animations/fade-in-up.animation';
import { stagger40ms } from 'src/@core/animations/stagger.animation';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { permissions } from 'src/app/shared/helpers/roles-permissions';
import { EnterpriseAdminService } from '../enterprise-admin/enterprise-admin.service';

@Component({
  selector: 'hour4u-admin-agents',
  templateUrl: './agents.component.html',
  styleUrls: ['./agents.component.scss'],
  animations: [
    fadeInUp400ms,
    stagger40ms,
    fadeInRight400ms
  ],
})
export class AgentsComponent implements OnInit {
  permissions = permissions;
  profile = this.appService.getCurrentAccount();
  agents: any[] = [];
  filteredAgents: any[] = [];
  typeFilter: string = 'all';
  isLoading = false;
  diameter = 40;
  loaderColor: string;
  mode = 'indeterminate';
  pageIndex = 1;
  pageSize = 50;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  currentRange = '';
  pagination: any = null;

  typeFilters = [
    { value: 'all', label: 'All' },
    { value: 'voice', label: 'Voice' },
    { value: 'whatsapp', label: 'WhatsApp' }
  ];

  constructor(
    private router: Router,
    public enterpriseAdminService: EnterpriseAdminService,
    private appService: AppService,
    private notify: NotificationService
  ) {}

  ngOnInit() {
    this.getAllAgent();
  }

  createAgent() {
    this.router.navigate(['/agents/agent-create']);
  }

  editAgent(agentId: string) {
    this.router.navigate(['/agents/agent-create', agentId]);
  }

  goToAgent(agentId: string) {
    this.router.navigate(['/agents/agent-view', agentId]);
  }

  normalizeType(raw: string): string {
    const t = (raw || '').toLowerCase().replace(/-/g, '_');
    if (t.includes('whatsapp') || t === 'wa') {
      return 'whatsapp';
    }
    if (t.includes('inbound') || t.includes('outbound') || t.includes('voice')) {
      return 'voice';
    }
    return t || 'voice';
  }

  typeLabel(agentOrRaw: any): string {
    if (agentOrRaw && typeof agentOrRaw === 'object') {
      const raw = agentOrRaw.agent_type || agentOrRaw.agentType || '';
      const t = this.normalizeType(raw);
      if (t === 'whatsapp') {
        return 'WhatsApp';
      }
      if (agentOrRaw.use_as_inbound || String(raw).toLowerCase().includes('inbound')) {
        return 'Voice (incoming)';
      }
      return 'Voice';
    }
    const t = this.normalizeType(agentOrRaw);
    const found = this.typeFilters.find(f => f.value === t);
    return found ? found.label : (agentOrRaw || 'Agent');
  }

  applyFilter() {
    if (this.typeFilter === 'all') {
      this.filteredAgents = this.agents.slice();
      return;
    }
    this.filteredAgents = this.agents.filter(
      a => this.normalizeType(a.agent_type || a.agentType) === this.typeFilter
    );
  }

  onFilterChange(value: string) {
    this.typeFilter = value;
    this.applyFilter();
  }

  getAllAgent() {
    this.isLoading = true;
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;

    if (!entityId) {
      console.error('Entity ID is missing!');
      this.isLoading = false;
      return;
    }

    this.agents = [];
    this.enterpriseAdminService.getAllAgentsByEntityId(entityId, this.pageSize, this.pageIndex).subscribe(
      response => {
        const items = (response && response.items) || [];
        this.agents = items.map(agent => ({
          ...agent,
          profilePic: '/assets/agent-dp.webp'
        }));
        this.pagination = response.pagination;
        this.updateRange();
        this.applyFilter();
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        console.error('Failed to fetch agents:', error);
        this.notify.showMessage('Failed to load agents', 2500);
      }
    );
  }

  deleteAgent(agent: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    if (!entityId || !agent || !agent.agent_id) {
      return;
    }
    if (!confirm(`Delete agent "${agent.agent_name}"?`)) {
      return;
    }
    this.enterpriseAdminService.deleteAgent(entityId, agent.agent_id).subscribe(
      () => {
        this.notify.showMessage('Agent deleted', 2000);
        this.getAllAgent();
      },
      err => {
        console.error(err);
        this.notify.showMessage('Delete failed', 2500);
      }
    );
  }

  onNext() {
    if (this.pagination && this.pagination.currentPageNum < this.pagination.totalPages) {
      this.pageIndex = this.pagination.currentPageNum + 1;
      this.getAllAgent();
    }
  }

  onPrev() {
    if (this.pagination && this.pagination.currentPageNum > 1) {
      this.pageIndex = this.pagination.currentPageNum - 1;
      this.getAllAgent();
    }
  }

  updateRange() {
    if (this.pagination && this.pagination.currentPageNum && this.pagination.currentPageSize && this.pagination.totalElements) {
      const start = (this.pagination.currentPageNum - 1) * this.pagination.currentPageSize + 1;
      const end = Math.min(
        this.pagination.currentPageNum * this.pagination.currentPageSize,
        this.pagination.totalElements
      );
      this.currentRange = `${start} - ${end} of ${this.pagination.totalElements} records`;
    } else {
      this.currentRange = `${this.filteredAgents.length} agents`;
    }
  }

  onPageSizeChange(value: any) {
    this.pageSize = parseInt(value, 10);
    this.pageIndex = 1;
    this.getAllAgent();
  }
}
